import React, { useRef, useState } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { StarIcon, ChevronRightIcon } from "./icons";
import {
  AppProfileProps,
  AppProfileAppItem,
  AppProfileHistogram,
  AppProfileReview,
} from "./types";

const HISTOGRAM_ROWS: Array<{ stars: number; key: keyof AppProfileHistogram }> =
  [
    { stars: 5, key: "five" },
    { stars: 4, key: "four" },
    { stars: 3, key: "three" },
    { stars: 2, key: "two" },
    { stars: 1, key: "one" },
  ];

/** Five stars with the first `Math.round(score)` filled. */
const Stars: React.FC<{ score?: number | null; className?: string }> = ({
  score,
  className,
}) => {
  const filled = Math.round(score ?? 0);

  return (
    <span
      className={cs(styles.stars, className)}
      aria-label={`${score ?? 0} of 5`}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <StarIcon
          key={i}
          className={cs(styles.star, { [styles.starEmpty]: i > filled })}
        />
      ))}
    </span>
  );
};

/** Card surface with a title and an optional "View details" action. */
const Section: React.FC<{
  title: string;
  action?: { label: string; onClick?: () => void };
  className?: string;
  children: React.ReactNode;
}> = ({ title, action, className, children }) => (
  <section className={cs(styles.section, className)}>
    <header className={styles.sectionHead}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {action && (
        <button
          type="button"
          className={styles.sectionAction}
          onClick={action.onClick}
        >
          {action.label}
          <ChevronRightIcon className={styles.actionChevron} />
        </button>
      )}
    </header>
    {children}
  </section>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <p className={styles.empty}>{text}</p>
);

const AppRow: React.FC<{ app: AppProfileAppItem }> = ({ app }) => (
  <button
    type="button"
    className={styles.appRow}
    onClick={app.onClick}
    disabled={!app.onClick}
  >
    {app.icon ? (
      <img className={styles.appRowIcon} src={app.icon} alt="" />
    ) : (
      <span className={cs(styles.appRowIcon, styles.appRowIconFallback)}>
        {app.title.slice(0, 1).toUpperCase()}
      </span>
    )}
    <span className={styles.appRowText}>
      <span className={styles.appRowTitle}>{app.title}</span>
      {(app.caption ?? app.developer) && (
        <span className={styles.appRowCaption}>
          {app.caption ?? app.developer}
        </span>
      )}
    </span>
    {app.score != null && (
      <span className={styles.appRowScore}>
        <StarIcon className={styles.appRowStar} />
        {app.score.toFixed(1)}
      </span>
    )}
  </button>
);

const ReviewItem: React.FC<{ review: AppProfileReview }> = ({ review }) => (
  <li className={styles.review}>
    <div className={styles.reviewHead}>
      <Stars score={review.score} className={styles.reviewStars} />
      <span className={styles.reviewAuthor}>
        {review.userName ?? "Anonymous"}
      </span>
      {review.date && <span className={styles.reviewDate}>{review.date}</span>}
    </div>
    {review.title && <p className={styles.reviewTitle}>{review.title}</p>}
    {review.text && <p className={styles.reviewText}>{review.text}</p>}
  </li>
);

/**
 * App Profile — the FoxData-style app card content: screenshots + description,
 * latest version, About table, ratings & reviews, and a side column with the
 * developer's other apps and similar apps.
 *
 * Pure presentational: every value comes from props, and the ratings/reviews
 * blocks can be swapped for the live containers through `ratingSlot`/`reviewsSlot`.
 * The host owns the surface (modal today, full-screen route later).
 */
export const AppProfile: React.FC<AppProfileProps> = ({
  loading = false,
  headerSlot,
  screenshots,
  description,
  descriptionLanguageSlot,
  descriptionClampLines = 6,
  version,
  onViewVersions,
  about,
  rating,
  onViewRatings,
  reviews,
  onViewReviews,
  ratingSlot,
  reviewsSlot,
  developerName,
  developerApps,
  similarApps,
  emptyText = "No data yet",
  className,
}) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const galleryRef = useRef<HTMLDivElement>(null);

  const scrollGallery = (direction: -1 | 1) => {
    const node = galleryRef.current;
    if (!node) return;

    node.scrollTo({
      left: node.scrollLeft + direction * node.clientWidth * 0.8,
      behavior: "smooth",
    });
  };

  if (loading) {
    return (
      <div className={cs(styles.root, className)}>
        {headerSlot}
        <div className={styles.grid}>
          <div className={styles.column}>
            <div
              className={cs(
                styles.section,
                styles.skeleton,
                styles.skeletonTall,
              )}
            />
            <div className={cs(styles.section, styles.skeleton)} />
            <div className={cs(styles.section, styles.skeleton)} />
          </div>
          <div className={styles.column}>
            <div className={cs(styles.section, styles.skeleton)} />
            <div className={cs(styles.section, styles.skeleton)} />
          </div>
        </div>
      </div>
    );
  }

  const histogram = rating?.histogram;
  const histogramTotal = histogram
    ? HISTOGRAM_ROWS.reduce((sum, row) => sum + (histogram[row.key] || 0), 0)
    : 0;

  return (
    <div className={cs(styles.root, className)}>
      {headerSlot}

      <div className={styles.grid}>
        {/* ---- main column ---- */}
        <div className={styles.column}>
          <Section title="App info">
            {screenshots && screenshots.length > 0 ? (
              <div className={styles.gallery}>
                <div className={styles.galleryStrip} ref={galleryRef}>
                  {screenshots.map((shot, i) => (
                    <img
                      key={shot.url || i}
                      className={styles.screenshot}
                      src={shot.url}
                      alt={shot.alt ?? ""}
                      loading="lazy"
                    />
                  ))}
                </div>
                {screenshots.length > 2 && (
                  <>
                    <button
                      type="button"
                      aria-label="Previous screenshots"
                      className={cs(
                        styles.galleryArrow,
                        styles.galleryArrowPrev,
                      )}
                      onClick={() => scrollGallery(-1)}
                    >
                      <ChevronRightIcon className={styles.galleryChevron} />
                    </button>
                    <button
                      type="button"
                      aria-label="Next screenshots"
                      className={cs(
                        styles.galleryArrow,
                        styles.galleryArrowNext,
                      )}
                      onClick={() => scrollGallery(1)}
                    >
                      <ChevronRightIcon className={styles.galleryChevron} />
                    </button>
                  </>
                )}
              </div>
            ) : (
              <Empty text={emptyText} />
            )}

            {descriptionLanguageSlot && (
              <div className={styles.descriptionTools}>
                {descriptionLanguageSlot}
              </div>
            )}

            {description ? (
              <>
                <p
                  className={cs(styles.description, {
                    [styles.descriptionClamped]: !descriptionExpanded,
                  })}
                  style={
                    descriptionExpanded
                      ? undefined
                      : ({
                          WebkitLineClamp: descriptionClampLines,
                        } as React.CSSProperties)
                  }
                >
                  {description}
                </p>
                <button
                  type="button"
                  className={styles.textButton}
                  onClick={() => setDescriptionExpanded((v) => !v)}
                >
                  {descriptionExpanded ? "Show less" : "Show more"}
                </button>
              </>
            ) : (
              <Empty text={emptyText} />
            )}
          </Section>

          <Section
            title="Recent version"
            action={
              onViewVersions
                ? { label: "All versions", onClick: onViewVersions }
                : undefined
            }
          >
            {version?.version || version?.notes ? (
              <>
                <div className={styles.versionHead}>
                  <span className={styles.versionNumber}>
                    {version.version ?? "--"}
                  </span>
                  {version.releasedAt && (
                    <span className={styles.versionDate}>
                      {version.releasedAt}
                    </span>
                  )}
                </div>
                {version.notes ? (
                  <p className={styles.versionNotes}>{version.notes}</p>
                ) : (
                  <Empty text={emptyText} />
                )}
              </>
            ) : (
              <Empty text={emptyText} />
            )}
          </Section>

          <Section title="About">
            {about && about.length > 0 ? (
              <dl className={styles.about}>
                {about.map((row) => (
                  <div className={styles.aboutRow} key={row.label}>
                    <dt className={styles.aboutLabel}>{row.label}</dt>
                    <dd className={styles.aboutValue}>{row.value ?? "--"}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <Empty text={emptyText} />
            )}
          </Section>
        </div>

        {/* ---- side column ---- */}
        <div className={styles.column}>
          <Section
            title="Ratings"
            action={
              onViewRatings
                ? { label: "View details", onClick: onViewRatings }
                : undefined
            }
          >
            {ratingSlot ??
              (rating ? (
                <div className={styles.rating}>
                  <div className={styles.ratingSummary}>
                    <span className={styles.ratingScore}>
                      {rating.score != null ? rating.score.toFixed(1) : "--"}
                    </span>
                    <Stars score={rating.score} />
                    <span className={styles.ratingCount}>
                      {rating.reviewsCount != null
                        ? `${rating.reviewsCount.toLocaleString("en-US")} ratings`
                        : ""}
                    </span>
                  </div>

                  {histogram && histogramTotal > 0 && (
                    <div className={styles.histogram}>
                      {HISTOGRAM_ROWS.map((row) => {
                        const value = histogram[row.key] || 0;
                        const percent = Math.round(
                          (value / histogramTotal) * 100,
                        );

                        return (
                          <div className={styles.histogramRow} key={row.stars}>
                            <span className={styles.histogramLabel}>
                              {row.stars}★
                            </span>
                            <span className={styles.histogramTrack}>
                              <span
                                className={styles.histogramBar}
                                style={{ width: `${percent}%` }}
                              />
                            </span>
                            <span className={styles.histogramValue}>
                              {percent}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <Empty text={emptyText} />
              ))}
          </Section>

          <Section
            title="Recent reviews"
            action={
              onViewReviews
                ? { label: "View details", onClick: onViewReviews }
                : undefined
            }
          >
            {reviewsSlot ??
              (reviews && reviews.length > 0 ? (
                <ul className={styles.reviews}>
                  {reviews.map((review, i) => (
                    <ReviewItem key={review.id ?? i} review={review} />
                  ))}
                </ul>
              ) : (
                <Empty text={emptyText} />
              ))}
          </Section>

          <Section
            title={
              developerName
                ? `More by ${developerName}`
                : "More by this developer"
            }
          >
            {developerApps && developerApps.length > 0 ? (
              <div className={styles.appList}>
                {developerApps.map((app, i) => (
                  <AppRow key={app.id ?? i} app={app} />
                ))}
              </div>
            ) : (
              <Empty text={emptyText} />
            )}
          </Section>

          <Section title="Similar apps">
            {similarApps && similarApps.length > 0 ? (
              <div className={styles.appList}>
                {similarApps.map((app, i) => (
                  <AppRow key={app.id ?? i} app={app} />
                ))}
              </div>
            ) : (
              <Empty text={emptyText} />
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default AppProfile;
